import "./assets/styles/mc-design-tokens.css";
import "element-plus/theme-chalk/dark/css-vars.css";
import "./index.css";
import "video.js/dist/video-js.css";

import { initTheme } from "@/composables/theme";
initTheme();

import { createApp } from "vue";
import { createPinia } from "pinia";
import * as Sentry from "@sentry/vue";

import App from "./App.vue";
import router from "./router";
import i18n from "./i18n";

import { autofocus } from "./directives/autofocus";
import { keyclick } from "./directives/keyclick";
import { hscroll } from "./directives/hscroll";
import showpass from "./directives/showpass";
import { autoAnimatePlugin } from "@formkit/auto-animate/vue";

import VueAwesomePaginate from "vue-awesome-paginate";
import "vue-awesome-paginate/dist/style.css";

const app = createApp(App);

Sentry.init({
  app,
  dsn: "https://a9893957805245c65fe289eb94244aff@o4511772029419520.ingest.de.sentry.io/4511772038135888",
  environment: import.meta.env.VITE_ENVIRONMENT,
  release: __APP_VERSION__,
  integrations: [Sentry.replayIntegration()],
  // Session Replay: record 10% of all sessions and every session with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  beforeSend(event, hint) {
    // Errors flagged by comm.js as an expected session expiry (the user is
    // already being redirected to the login page) are not reportable
    if (hint?.originalException?.sessionExpired) return null;
    return event;
  },
  dataCollection: {
    // To disable sending user data and HTTP bodies, uncomment the lines below. For more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/vue/configuration/options/#dataCollection
    // userInfo: false,
    // httpBodies: []
  }
});

// Redact credentials (auth tokens, passwords...) so they never reach Sentry
function redactSensitiveState(value) {
  if (Array.isArray(value)) return value.map(redactSensitiveState);
  if (value === null || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, val]) => [
      key,
      /token|password|secret|credential/i.test(key)
        ? "[Filtered]"
        : redactSensitiveState(val)
    ])
  );
}

const pinia = createPinia();
pinia.use(
  Sentry.createSentryPiniaPlugin({
    stateTransformer: redactSensitiveState
  })
);

app.use(pinia);
app.use(router);
app.use(i18n);
app.use(autoAnimatePlugin);
app.use(VueAwesomePaginate);

app.directive("focus", autofocus);
app.directive("keyclick", keyclick);
app.directive("hscroll", hscroll);
app.directive("showpass", showpass);

app.mount("#app");
